/**
 * i18n setup — react-i18next + browser language detector.
 *
 * Resources are bundled at build time (small enough not to need async
 * loading). Add a language: create `locales/<code>/*.json`, export an
 * index, register in `resources` + `SUPPORTED_LANGUAGES`.
 *
 * Persistence: chosen language is stored in localStorage as
 * `pulsar-lang`. On first visit we fall back to `navigator.language`
 * mapped to a supported code, else English.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import en from './locales/en'
import ru from './locales/ru'

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
] as const

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code']

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en, ru },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    defaultNS: 'common',
    ns: ['common', 'landing', 'auth', 'app'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'pulsar-lang',
    },
    react: { useSuspense: false },
  })

// Keep `<html lang>` in sync — improves a11y + SEO consistency.
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng
})

export default i18n
